import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../auth";
import { ErrorBanner, Modal } from "../../components";
import {
  createTelegramRecipient,
  deleteTelegramRecipient,
  fetchTelegramRecipients,
  testTelegramRecipient,
  updateTelegramRecipient,
  updateTelegramRecipientSettings,
} from "../../api";
import type {
  TelegramBotKey,
  TelegramRecipientItem,
  TelegramRecipientRequest,
} from "../../api";

interface BotConfig {
  key: TelegramBotKey;
  title: string;
  url: string;
  ordersDefault: boolean;
  adminDefault: boolean;
}
const BOTS: BotConfig[] = [
  {
    key: "payments",
    title: "Payments",
    url: "https://t.me/vsdfvsdfvsdfvsdfvsdfvbot",
    ordersDefault: true,
    adminDefault: false,
  },
  {
    key: "payments_admin",
    title: "Payments Admin",
    url: "https://t.me/Shsgegehejeeiewh2826bot",
    ordersDefault: false,
    adminDefault: true,
  },
];

function formatDate(raw: string | null): string {
  if (!raw) return "—";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return new Intl.DateTimeFormat("ru-RU", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function buildPayload(
  chatId: string,
  title: string,
  enabled: boolean,
): TelegramRecipientRequest | null {
  const id = chatId.trim();
  const name = title.trim();
  if (!/^\d+$/.test(id) || /^0+$/.test(id) || !name) return null;
  return {chatId: id, title: name, enabled};
}

function deliveryStatus(item: TelegramRecipientItem): React.JSX.Element {
  if (item.lastError) {
    return (
      <>
        <strong className="telegram-status-error">Ошибка</strong>
        <div className="cell-subtle">{item.lastError}</div>
        <div className="cell-subtle">{formatDate(item.lastErrorAt)}</div>
      </>
    );
  }
  if (item.lastOkAt) {
    return (
      <>
        <strong className="telegram-status-ok">Доставлено</strong>
        <div className="cell-subtle">{formatDate(item.lastOkAt)}</div>
      </>
    );
  }
  return <span className="cell-subtle">Ещё не отправлялось</span>;
}

function BotStatus({
  config,
  endpoint,
  enabled,
}: {
  config: BotConfig;
  endpoint: string;
  enabled: boolean;
}): React.JSX.Element {
  let title: React.ReactNode;
  let details: string;
  let tone = "off";
  const link = (
    <a href={config.url} target="_blank" rel="noreferrer noopener">
      {config.url}
    </a>
  );
  if (!endpoint && !enabled) {
    title = <>Бот выключен · {link}</>;
    details = "Бот отключён настройкой сервера. Получателей можно подготовить заранее.";
  } else if (!endpoint) {
    title = <>Токен этого бота не настроен на сервере · {link}</>;
    details = "Получателей можно подготовить заранее.";
  } else if (endpoint !== "api.telegram.org") {
    tone = "fake";
    title = <>Тестовый Bot API: {endpoint} · {link}</>;
    details = "Сообщения принимает подставной сервер стенда и наружу не отправляет.";
  } else {
    tone = "live";
    title = <>Бот включён · {link}</>;
    details = "Для нового получателя откройте бота и нажмите Start, чтобы Telegram разрешил первое сообщение.";
  }
  return (
    <section
      className={`telegram-recipients-hint ${tone}`}
      data-bot-endpoint={endpoint}
    >
      <div>
        <strong>{title}</strong>
        <span>{details}</span>
        <span>
          В этом блоке настраиваются только получатели и события бота «{config.title}».
        </span>
      </div>
    </section>
  );
}

function BotRecipientsBlock({ token, config }: { token: string | null; config: BotConfig }): React.JSX.Element {
  const [items, setItems] = useState<TelegramRecipientItem[]>([]);
  const [botEndpoint, setBotEndpoint] = useState("");
  const [botEnabled, setBotEnabled] = useState(false);
  const [notifyOrders, setNotifyOrders] = useState(config.ordersDefault);
  const [notifyAdminNotifications, setNotifyAdminNotifications] = useState(config.adminDefault);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [success, setSuccess] = useState("");
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState<unknown>(null);
  const [newChatID, setNewChatID] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState<unknown>(null);
  const [editTarget, setEditTarget] = useState<TelegramRecipientItem | null>(null);
  const [editChatID, setEditChatID] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [editEnabled, setEditEnabled] = useState(true);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<unknown>(null);
  const [deleteTarget, setDeleteTarget] = useState<TelegramRecipientItem | null>(null);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteError, setDeleteError] = useState<unknown>(null);
  const [testingID, setTestingID] = useState("");

  const reload = useCallback((): void => {
    if (!token) return;
    setLoading(true);
    setError(null);
    fetchTelegramRecipients(token, config.key)
      .then((resp) => {
        setItems(resp.items);
        setBotEndpoint(resp.botEndpoint ?? "");
        setBotEnabled(resp.botEnabled ?? false);
        setNotifyOrders(resp.notifyOrders ?? config.ordersDefault);
        setNotifyAdminNotifications(
          resp.notifyAdminNotifications ?? config.adminDefault,
        );
      })
      .catch(setError)
      .finally(() => setLoading(false));
  }, [config, token]);

  useEffect(() => {
    reload();
  }, [reload]);
  const invalid =
    "Укажите личный telegram id (положительное число) и название. Группы и каналы не поддерживаются.";

  const submitCreate = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!token) {
      setCreateError("Требуется повторный вход.");
      return;
    }
    const payload = buildPayload(newChatID, newTitle, true);
    if (!payload) {
      setCreateError(invalid);
      return;
    }
    setCreateSaving(true);
    setCreateError(null);
    createTelegramRecipient(token, payload, config.key)
      .then(() => {
        setNewChatID("");
        setNewTitle("");
        setSuccess("Получатель добавлен. Проверьте доставку кнопкой «Проверить».");
        reload();
      })
      .catch(setCreateError)
      .finally(() => setCreateSaving(false));
  };

  const openEdit = (item: TelegramRecipientItem): void => {
    setEditTarget(item);
    setEditChatID(item.chatId);
    setEditTitle(item.title);
    setEditEnabled(item.enabled);
    setEditError(null);
  };

  const submitEdit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!token || !editTarget) return;
    const payload = buildPayload(editChatID, editTitle, editEnabled);
    if (!payload) {
      setEditError(invalid);
      return;
    }
    setEditSaving(true);
    setEditError(null);
    updateTelegramRecipient(token, editTarget.id, payload, config.key)
      .then(() => {
        setEditTarget(null);
        setSuccess("Получатель обновлён.");
        reload();
      })
      .catch(setEditError)
      .finally(() => setEditSaving(false));
  };

  const toggleEnabled = (item: TelegramRecipientItem): void => {
    if (!token) return;
    updateTelegramRecipient(
      token,
      item.id,
      {chatId: item.chatId, title: item.title, enabled: !item.enabled},
      config.key,
    )
      .then(() => {
        setSuccess(
          item.enabled
            ? `Получатель «${item.title}» выключен.`
            : `Получатель «${item.title}» включён.`,
        );
        reload();
      })
      .catch(setError);
  };

  const runTest = (item: TelegramRecipientItem): void => {
    if (!token) return;
    setTestingID(item.id);
    setError(null);
    testTelegramRecipient(token, item.id, config.key)
      .then((result) => {
        const via = result.botEndpoint ? ` через ${result.botEndpoint}` : "";
        setSuccess(
          result.ok
            ? `Сообщение доставлено получателю «${item.title}»${via}.`
            : `Не доставлено получателю «${item.title}»${via}: ${result.error ?? "причина неизвестна"}`,
        );
        reload();
      })
      .catch(setError)
      .finally(() => setTestingID(""));
  };

  const confirmDelete = (): void => {
    if (!token || !deleteTarget) return;
    setDeleteSaving(true);
    setDeleteError(null);
    deleteTelegramRecipient(token, deleteTarget.id, config.key)
      .then(() => {
        setDeleteTarget(null);
        setSuccess("Получатель удалён.");
        reload();
      })
      .catch(setDeleteError)
      .finally(() => setDeleteSaving(false));
  };

  const saveSettings = (): void => {
    if (!token) return;
    setSettingsSaving(true);
    setSettingsError(null);
    updateTelegramRecipientSettings(
      token,
      {notifyOrders, notifyAdminNotifications},
      config.key,
    )
      .then(() => setSuccess("Настройки уведомлений сохранены."))
      .catch(setSettingsError)
      .finally(() => setSettingsSaving(false));
  };
  const enabledCount = items.filter((item) => item.enabled).length;
  return (
    <section className="telegram-bot-block">
      <div className="telegram-bot-block-header">
        <div>
          <h3>{config.title}</h3>
          <p className="page-subtitle">
            {items.length} получателей, включено {enabledCount}
          </p>
        </div>
      </div>
      <BotStatus config={config} endpoint={botEndpoint} enabled={botEnabled} />
      <section className="telegram-settings-card">
        <div>
          <strong>Какие события отправлять</strong>
          <span className="cell-subtle">Настройки действуют только для этого бота.</span>
        </div>
        <label className="toggle-row">
          <input type="checkbox" checked={notifyOrders} onChange={(e) => setNotifyOrders(e.target.checked)} />
          Заявки
        </label>
        <label className="toggle-row">
          <input type="checkbox" checked={notifyAdminNotifications} onChange={(e) => setNotifyAdminNotifications(e.target.checked)} />
          Уведомления админки
        </label>
        <button className="btn btn-primary btn-sm" type="button" onClick={saveSettings} disabled={settingsSaving}>
          {settingsSaving ? "Сохранение..." : "Сохранить"}
        </button>
      </section>
      <ErrorBanner error={settingsError} title="Настройки не сохранены" />
      <div className="telegram-recipients-grid">
        <section className="telegram-recipients-form-section">
          <div className="section-header"><h3>Добавить получателя</h3></div>
          <form className="telegram-recipients-form" onSubmit={submitCreate}>
            <ErrorBanner error={createError} title="Получатель не добавлен" />
            <div className="form-group">
              <label htmlFor={`telegram-chat-id-${config.key}`}>Telegram id</label>
              <input id={`telegram-chat-id-${config.key}`} type="text" inputMode="numeric" value={newChatID} onChange={(e) => setNewChatID(e.target.value)} placeholder="123456789" autoComplete="off" />
            </div>
            <div className="form-group">
              <label htmlFor={`telegram-title-${config.key}`}>Название</label>
              <input id={`telegram-title-${config.key}`} type="text" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Дежурный оператор" maxLength={128} autoComplete="off" />
            </div>
            <button className="btn btn-primary" type="submit" disabled={createSaving}>{createSaving ? "Добавление..." : "Добавить"}</button>
          </form>
        </section>
        <section className="telegram-recipients-table-section">
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Название</th><th>Telegram id</th><th>Уведомления</th><th>Последняя доставка</th><th /></tr></thead>
              <tbody>
                {loading && <tr><td colSpan={5} className="empty-state">Загрузка получателей...</td></tr>}
                {!loading && items.length === 0 && <tr><td colSpan={5} className="empty-state">Список пуст — уведомления никому не уходят.</td></tr>}
                {!loading && items.map((item) => (
                  <tr key={item.id}>
                    <td><strong>{item.title}</strong></td><td className="mono">{item.chatId}</td>
                    <td><button className={item.enabled ? "btn btn-ghost btn-sm" : "btn btn-primary btn-sm"} type="button" onClick={() => toggleEnabled(item)}>{item.enabled ? "Выключить" : "Включить"}</button></td>
                    <td className="telegram-status-cell">{deliveryStatus(item)}</td>
                    <td className="merchant-action-cell">
                      <button className="btn btn-ghost btn-sm" type="button" onClick={() => runTest(item)} disabled={testingID === item.id}>{testingID === item.id ? "Проверка..." : "Проверить"}</button>
                      <button className="btn btn-ghost btn-sm" type="button" onClick={() => openEdit(item)}>Редактировать</button>
                      <button className="btn btn-danger btn-sm" type="button" onClick={() => { setDeleteError(null); setDeleteTarget(item); }}>Удалить</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
      <Modal open={editTarget !== null} title="Редактировать получателя" onClose={() => setEditTarget(null)} sizeClassName="modal-sm">
        <form className="telegram-recipients-form" onSubmit={submitEdit}>
          <ErrorBanner error={editError} title="Получатель не обновлён" />
          <div className="form-group"><label htmlFor={`edit-telegram-chat-id-${config.key}`}>Telegram id</label><input id={`edit-telegram-chat-id-${config.key}`} type="text" inputMode="numeric" value={editChatID} onChange={(e) => setEditChatID(e.target.value)} autoComplete="off" /></div>
          <div className="form-group"><label htmlFor={`edit-telegram-title-${config.key}`}>Название</label><input id={`edit-telegram-title-${config.key}`} type="text" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} maxLength={128} autoComplete="off" /></div>
          <label className="toggle-row"><input type="checkbox" checked={editEnabled} onChange={(e) => setEditEnabled(e.target.checked)} />Получает уведомления</label>
          <div className="modal-actions"><button className="btn btn-ghost" type="button" onClick={() => setEditTarget(null)} disabled={editSaving}>Отмена</button><button className="btn btn-primary" type="submit" disabled={editSaving}>{editSaving ? "Сохранение..." : "Сохранить"}</button></div>
        </form>
      </Modal>
      <Modal open={deleteTarget !== null} title="Удалить получателя" onClose={() => setDeleteTarget(null)} sizeClassName="modal-sm">
        <ErrorBanner error={deleteError} title="Получатель не удалён" />
        {deleteTarget && <p className="modal-hint"><strong>{deleteTarget.title}</strong> ({deleteTarget.chatId}) перестанет получать уведомления «{config.title}».</p>}
        <div className="modal-actions"><button className="btn btn-ghost" type="button" onClick={() => setDeleteTarget(null)} disabled={deleteSaving}>Отмена</button><button className="btn btn-danger" type="button" onClick={confirmDelete} disabled={deleteSaving}>{deleteSaving ? "Удаление..." : "Удалить"}</button></div>
      </Modal>
    </section>
  );
}

export function AdminTelegramRecipientsPage(): JSX.Element {
  const {token} = useAuth();
  return (
    <div className="page admin-telegram-recipients">
      <div className="page-header">
        <div>
          <h2>Telegram</h2>
          <p className="page-subtitle">Два бота с отдельными получателями и настройками событий.</p>
        </div>
      </div>
      <ErrorBanner error={!token ? "Требуется повторный вход." : null} title="Telegram недоступен" />
      {BOTS.map((config) => <BotRecipientsBlock key={config.key} token={token} config={config} />)}
    </div>
  );
}
